/**
 * Board 3 E · Mark declined (460) — `docs/job-stages.md` §5, §8 E: the
 * customer said no some other way than the link. The quote closes as
 * **Declined** (the reason and what was said in `status_reason`), the job
 * moves to **Declined**, and History says *Marked declined by <name> — <reason>*
 * (`declineByOffice`: no `quote_responses` row, no bell).
 *
 * **Why**: the five reasons the customer picks on the link (`DECLINE_REASONS`),
 * one required — **Mark declined** (danger) waits for it. **What <first
 * name> said** is optional. `POST /quotes/:id/decline {reason, note?}`.
 */
import { useEffect, useState } from 'react'
import type { DeclineReason, JobActionsAnswer } from '@rowted/shared'
import { declineQuote, readJobActions } from './api.js'
import { MARK_DECLINED, quoteVersion } from './copy.js'
import { ChipRow, FieldLabel, Fields, PillChip, Refusal, StageBand, StagePopup, TextBox, messageOf } from './popup.js'
import { hasReason } from './rules.js'

export interface MarkDeclinedProps {
  open: boolean
  jobId: string
  /** The quote with the customer. */
  quoteId: string
  onClose: () => void
  /** After the server recorded it: the quote, the reason and what was said. */
  onDone: (answer: { quoteId: string; reason: DeclineReason; note: string | null }) => void
  phone?: boolean
}

export function MarkDeclined(props: MarkDeclinedProps) {
  if (!props.open) return null
  return <MarkDeclinedBox {...props} />
}

function MarkDeclinedBox({ jobId, quoteId, onClose, onDone, phone }: MarkDeclinedProps) {
  const [facts, setFacts] = useState<JobActionsAnswer | null>(null)
  const [reason, setReason] = useState<DeclineReason | null>(null)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    readJobActions(jobId, quoteId)
      .then((answer) => { if (live) setFacts(answer) })
      .catch((cause: unknown) => { if (live) setError(messageOf(cause)) })
    return () => { live = false }
  }, [jobId, quoteId])

  const quote = facts?.quotes.find((one) => one.id === quoteId) ?? null

  async function decline() {
    if (!reason) return
    setBusy(true)
    setError(null)
    try {
      const said = note.trim() || null
      await declineQuote(quoteId, reason, said)
      onDone({ quoteId, reason, note: said })
    } catch (cause) {
      setError(messageOf(cause))
    } finally {
      setBusy(false)
    }
  }

  return (
    <StagePopup
      width={460}
      title={quote ? MARK_DECLINED.title(quoteVersion(quote.reference, quote.version)) : ''}
      {...(facts ? { sub: MARK_DECLINED.sub(facts.firstName) } : {})}
      tone="danger"
      glyph="close"
      onClose={onClose}
      asDrawn="js-03 E"
      {...(phone === undefined ? {} : { phone })}
      buttons={[
        { label: MARK_DECLINED.cancel, tone: 'plain', onClick: onClose },
        { label: MARK_DECLINED.markDeclined, tone: 'danger', disabled: busy || !hasReason(reason), onClick: () => void decline() },
      ]}
    >
      <Fields>
        <div className="min-w-0">
          <FieldLabel id="decline-why" label={MARK_DECLINED.why} required />
          <ChipRow labelledBy="decline-why">
            {MARK_DECLINED.reasons.map((one) => (
              <PillChip key={one.value} label={one.label} on={reason === one.value} onClick={() => setReason(one.value)} />
            ))}
          </ChipRow>
        </div>
        <div className="min-w-0">
          <FieldLabel htmlFor="decline-said" label={MARK_DECLINED.said(facts?.firstName)} optional />
          <TextBox id="decline-said" value={note} onChange={setNote} />
        </div>
        <StageBand tone="grey">
          <b>{MARK_DECLINED.band.lead}</b> {MARK_DECLINED.band.rest}
        </StageBand>
        <Refusal message={error} />
      </Fields>
    </StagePopup>
  )
}
